import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { View, ViewStyle } from 'react-native'

import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { parseISO } from 'date-fns'
import { debounce } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { EnvelopeStatus } from '@lyrahealth-inc/shared-app-logic/src/models/document/Document'
import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { SignatureIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/SignatureIcon'
import { Tooltip } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/tooltip/Tooltip'
import { DocumentStatus } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/documentFilters/DocumentFiltersPopover'

import { DocumentItem } from '../ClientDocuments'

const FileNameContainer = styled.View(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  flexDirection: 'row',
  overflow: 'hidden',
  marginRight: theme.spacing['16px'],
}))

const SignatureIconWrapper = styled.View({
  flexShrink: 0,
  marginLeft: 6,
  marginTop: 2,
  alignSelf: 'center',
})

const FileNameStart = styled(BodyText)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flexShrink: 1,
  minWidth: 0,
})

const FileNameEnd = styled(BodyText)({
  whiteSpace: 'nowrap',
  flexShrink: 0,
})

const StatusCellContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
})

const StatusDot = styled.View<{ color: string }>(({ color }) => ({
  width: 8,
  height: 8,
  borderRadius: 4,
  backgroundColor: color,
}))

const FileNameCell: React.FC<{
  fileName: string
  documentId: string
  isRowHovered?: boolean
  isIncomplete?: boolean
  envelopeStatus?: EnvelopeStatus
}> = ({ fileName, documentId, isRowHovered, isIncomplete, envelopeStatus }) => {
  const { colors } = useTheme()
  const [isTruncated, setIsTruncated] = useState(false)
  const elementId = `file-name-${documentId}`
  const endChars = 10
  const showMiddleTruncation = fileName.length > endChars * 2

  useEffect(() => {
    const checkTruncation = () => {
      const startEl = document.getElementById(elementId)
      if (startEl) {
        setIsTruncated(startEl.scrollWidth > startEl.clientWidth)
      }
    }

    const debouncedCheck = debounce(checkTruncation, 100)
    requestAnimationFrame(checkTruncation)
    window.addEventListener('resize', debouncedCheck)
    return () => {
      window.removeEventListener('resize', debouncedCheck)
      debouncedCheck.cancel()
    }
  }, [fileName, elementId])

  const textStyle = isRowHovered ? { textDecorationLine: 'underline' as const } : undefined
  const textColor = isIncomplete ? colors.textInactive : undefined

  let signatureIcon: React.ReactNode = null
  if (envelopeStatus) {
    const tooltipContent = envelopeStatus === EnvelopeStatus.COMPLETED ? 'Signature completed' : 'Signature required'
    signatureIcon = (
      <SignatureIconWrapper>
        <Tooltip content={tooltipContent} hoverEnabled placement='top'>
          <SignatureIcon
            height={16}
            width={16}
            fillColor={envelopeStatus === EnvelopeStatus.COMPLETED ? colors.textSuccess : colors.iconInactive}
          />
        </Tooltip>
      </SignatureIconWrapper>
    )
  }

  const content = (
    <FileNameContainer>
      {showMiddleTruncation ? (
        <>
          <FileNameStart id={elementId} style={textStyle} color={textColor}>
            {fileName.slice(0, -endChars)}
          </FileNameStart>
          <FileNameEnd style={textStyle} color={textColor}>
            {fileName.slice(-endChars)}
          </FileNameEnd>
        </>
      ) : (
        <FileNameStart id={elementId} style={textStyle} color={textColor}>
          {fileName}
        </FileNameStart>
      )}
      {signatureIcon}
    </FileNameContainer>
  )

  if (isTruncated) {
    return (
      <Tooltip
        content={fileName}
        hoverEnabled
        placement='top'
        wrapperStyle={{ flex: 1, minWidth: 0, display: 'flex' }}
        style={{ flex: 1, minWidth: 0, display: 'block' } as unknown as ViewStyle}
      >
        {content}
      </Tooltip>
    )
  }

  return content
}

const useClientDocumentsColumns = ({
  clientName,
  providerFullName,
}: {
  clientName: string
  providerFullName: string | null
}): ColumnDef<DocumentItem, string>[] => {
  const intl = useIntl()
  const { colors } = useTheme()

  const formatDate = useCallback(
    (dateStr: string) => {
      try {
        return intl.formatDate(parseISO(dateStr), { year: 'numeric', month: '2-digit', day: '2-digit' })
      } catch {
        return dateStr
      }
    },
    [intl],
  )

  return useMemo(() => {
    const columnHelper = createColumnHelper<DocumentItem>()

    return [
      columnHelper.accessor('fileName', {
        header: 'File Name',
        enableSorting: true,
        size: undefined,
        meta: { inlineComponent: true },
        cell: (ctx) => (
          <FileNameCell
            fileName={ctx.getValue()}
            documentId={ctx.row.original.id}
            isRowHovered={(ctx as { isRowHovered?: boolean }).isRowHovered}
            isIncomplete={ctx.row.original.status === DocumentStatus.INCOMPLETE}
            envelopeStatus={ctx.row.original.envelopeStatus}
          />
        ),
      }),
      columnHelper.accessor('type', {
        header: 'Type',
        enableSorting: true,
        size: 160,
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        enableSorting: true,
        size: 140,
        cell: (ctx) => {
          const status = ctx.getValue()
          const doc = ctx.row.original
          const isIncomplete = status === DocumentStatus.INCOMPLETE
          const statusDisplayText =
            isIncomplete && doc.totalSigners != null
              ? intl.formatMessage(
                  {
                    defaultMessage: 'Signature incomplete ({signedCount}/{totalSigners})',
                    description: 'Status text showing signature completion count',
                  },
                  { signedCount: doc.signedCount ?? 0, totalSigners: doc.totalSigners },
                )
              : status

          const isDraft = status === DocumentStatus.DRAFT

          let tooltipContent: string | undefined
          if (isDraft) {
            tooltipContent = intl.formatMessage(
              {
                defaultMessage: 'Draft saved by {name} {date}',
                description: 'Tooltip showing who saved the document as a draft',
              },
              { name: doc.addedBy, date: formatDate(doc.updatedOn) },
            )
          } else if (status === DocumentStatus.COMPLETED) {
            if (doc.envelopeStatus === EnvelopeStatus.COMPLETED) {
              tooltipContent = intl.formatMessage(
                {
                  defaultMessage: 'Signed by {name} {date}',
                  description: 'Tooltip showing when document was signed via DocuSign',
                },
                { name: clientName, date: formatDate(doc.updatedOn) },
              )
            } else if (doc.viewedDate) {
              tooltipContent = intl.formatMessage(
                {
                  defaultMessage: 'Viewed by {name} {date}',
                  description: 'Tooltip showing when document was viewed',
                },
                { name: clientName, date: formatDate(doc.viewedDate) },
              )
            }
          } else if (status === DocumentStatus.UPLOADED) {
            tooltipContent = intl.formatMessage(
              {
                defaultMessage: 'Uploaded by {name} {date}',
                description: 'Tooltip showing who uploaded the document',
              },
              { name: doc.addedBy, date: formatDate(doc.createDate) },
            )
          } else if (status === DocumentStatus.INCOMPLETE) {
            const { totalSigners, clientSignedDate, providerSignedDate } = doc
            const sentPart = intl.formatMessage(
              {
                defaultMessage: 'Sent by {name} {date}',
                description: 'Tooltip showing when document was sent',
              },
              { name: providerFullName, date: formatDate(doc.updatedOn) },
            )
            if (!totalSigners) {
              tooltipContent = sentPart
            } else if (totalSigners === 1) {
              tooltipContent = intl.formatMessage(
                {
                  defaultMessage: 'Waiting on client signature • Sent by {name} {date}',
                  description: 'Tooltip for a single-signer document awaiting client signature',
                },
                { name: providerFullName, date: formatDate(doc.updatedOn) },
              )
            } else {
              const clientSigned = clientSignedDate != null
              const providerSigned = providerSignedDate != null
              const sentDate = formatDate(doc.updatedOn)
              if (!clientSigned && !providerSigned) {
                tooltipContent = intl.formatMessage(
                  {
                    defaultMessage: 'Waiting on client and provider signature • Sent by {name} {date}',
                    description: 'Tooltip for a multi-signer document when neither party has signed',
                  },
                  { name: providerFullName, date: sentDate },
                )
              } else if (clientSigned && !providerSigned) {
                tooltipContent = intl.formatMessage(
                  {
                    defaultMessage:
                      'Signed by {clientName} {clientDate} • Waiting on provider signature • Sent by {senderName} {sentDate}',
                    description:
                      'Tooltip for a multi-signer document when the client has signed and the provider signature is pending',
                  },
                  { clientName, clientDate: formatDate(clientSignedDate), senderName: providerFullName, sentDate },
                )
              } else if (!clientSigned && providerSigned) {
                tooltipContent = intl.formatMessage(
                  {
                    defaultMessage:
                      'Waiting on client signature • Signed by {providerName} {providerDate} • Sent by {senderName} {sentDate}',
                    description:
                      'Tooltip for a multi-signer document when the provider has signed and the client signature is pending',
                  },
                  {
                    providerName: providerFullName ?? '',
                    providerDate: formatDate(providerSignedDate),
                    senderName: providerFullName,
                    sentDate,
                  },
                )
              } else {
                tooltipContent = intl.formatMessage(
                  {
                    defaultMessage:
                      'Signed by {clientName} {clientDate} • Signed by {providerName} {providerDate} • Sent by {senderName} {sentDate}',
                    description: 'Tooltip for a multi-signer document when both parties have signed',
                  },
                  {
                    clientName,
                    clientDate: formatDate(clientSignedDate!),
                    providerName: providerFullName ?? '',
                    providerDate: formatDate(providerSignedDate!),
                    senderName: providerFullName,
                    sentDate,
                  },
                )
              }
            }
          } else if (status === DocumentStatus.DECLINED) {
            tooltipContent = intl.formatMessage(
              {
                defaultMessage: 'Declined by {name} {date}',
                description: 'Tooltip showing when document was declined by the client',
              },
              { name: clientName, date: formatDate(doc.updatedOn) },
            )
          }

          const statusColorMap: Partial<Record<DocumentStatus, string>> = {
            [DocumentStatus.DRAFT]: colors.textSecondary,
            [DocumentStatus.INCOMPLETE]: colors.textWarning,
          }
          const statusColor = statusColorMap[status]

          const isSignatureIncomplete = isIncomplete && doc.totalSigners != null
          let statusLabelNode: React.ReactNode
          if (isSignatureIncomplete) {
            statusLabelNode = (
              <View>
                <BodyText
                  text={intl.formatMessage({
                    defaultMessage: 'Signature',
                    description:
                      'First line of status column text for a document with incomplete multi-party signatures',
                  })}
                  color={statusColor}
                />
                <BodyText
                  text={intl.formatMessage(
                    {
                      defaultMessage: 'incomplete ({signedCount}/{totalSigners})',
                      description:
                        'Second line of status column text for a document with incomplete multi-party signatures',
                    },
                    { signedCount: doc.signedCount ?? 0, totalSigners: doc.totalSigners },
                  )}
                  color={statusColor}
                />
              </View>
            )
          } else {
            statusLabelNode = <BodyText text={statusDisplayText} color={statusColor} />
          }
          const statusContent = (
            <StatusCellContainer>
              {isIncomplete && <StatusDot color={colors.textWarning} />}
              {statusLabelNode}
            </StatusCellContainer>
          )

          if (!tooltipContent) {
            return statusContent
          }

          return (
            <Tooltip content={tooltipContent} hoverEnabled placement='top'>
              {statusContent}
            </Tooltip>
          )
        },
      }),
      columnHelper.accessor('updatedOn', {
        header: 'Updated On',
        enableSorting: true,
        size: 160,
        cell: (ctx) => {
          const dateValue = ctx.getValue()
          if (!dateValue) return ''
          return formatDate(dateValue)
        },
      }),
      columnHelper.accessor('addedBy', {
        header: 'Added By',
        enableSorting: true,
        size: 180,
        cell: (ctx) => {
          const uploadedForProvider = ctx.row.original.uploadedForProvider
          const uploadedForClient = ctx.row.original.uploadedForClient
          return (
            <View>
              <BodyText text={ctx.getValue()} />
              {uploadedForProvider && (
                <BodyText
                  color={colors.textSecondary}
                  size={BodyTextSize.CAPTION}
                  text={intl.formatMessage(
                    {
                      defaultMessage: 'for {providerName}',
                      description: 'Indicates which provider a client document was uploaded for',
                    },
                    { providerName: uploadedForProvider },
                  )}
                />
              )}
              {uploadedForClient && (
                <BodyText
                  color={colors.textSecondary}
                  size={BodyTextSize.CAPTION}
                  text={intl.formatMessage(
                    {
                      defaultMessage: 'for {clientName}',
                      description: 'Indicates which client a provider document was uploaded for in DBT Teens program',
                    },
                    { clientName: uploadedForClient },
                  )}
                />
              )}
            </View>
          )
        },
      }),
    ]
  }, [colors.textWarning, colors.textSecondary, intl, clientName, providerFullName, formatDate])
}

export default useClientDocumentsColumns
